import React, {
 useState, useMemo
} from 'react';
import {
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from 'shared/components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Pencil, Trash2, Calculator
} from 'lucide-react';
import {
 SchemaCalculation, SchemaField
} from '../../types/schema.types';
import {
 dataTypes
} from 'builders/builderApp/DataBuilder/Tables/components/table-fields/constants';

interface SchemaCalculationsTabProps {
    calculations: SchemaCalculation[];
    setCalculations: React.Dispatch<React.SetStateAction<SchemaCalculation[]>>;
    fields: SchemaField[];
    setFields: React.Dispatch<React.SetStateAction<SchemaField[]>>;
}

const emptyCalculation: Omit<SchemaCalculation, 'id'> = {
    fieldName: '',
    formula: '',
    dependencies: [],
    returnType: 'string',
};

let calcIdCounter = 0;

const SchemaCalculationsTab: React.FC<SchemaCalculationsTabProps> = ({
    calculations, setCalculations, fields, setFields,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingCalc, setEditingCalc] = useState<SchemaCalculation | null>(null);
    const [formData, setFormData] = useState(emptyCalculation);

    // Auto-detect dependencies from formula
    const detectedDependencies = useMemo(() => {
        if (!formData.formula) return [];
        const fieldNames = fields
            .filter((f) => f.dataType !== 'equation')
            .map((f) => f.columnName);
        return fieldNames.filter((name) => formData.formula.includes(name));
    }, [formData.formula, fields]);

    const openAddModal = () => {
        setEditingCalc(null);
        setFormData(emptyCalculation);
        setShowModal(true);
    };

    const openEditModal = (calc: SchemaCalculation) => {
        setEditingCalc(calc);
        setFormData({
            fieldName: calc.fieldName,
            formula: calc.formula,
            dependencies: calc.dependencies,
            returnType: calc.returnType,
        });
        setShowModal(true);
    };

    const handleSave = () => {
        if (!formData.fieldName || !formData.formula) return;

        const calcData = {
            ...formData,
            dependencies: detectedDependencies,
        };

        if (editingCalc) {
            setCalculations((prev) =>
                prev.map((c) =>
                    c.id === editingCalc.id ? {
 ...c,
...calcData
} : c
                )
            );
            // Update the corresponding equation field
            setFields((prev) =>
                prev.map((f) =>
                    f.dataType === 'equation' && f.columnName === editingCalc.fieldName
                        ? {
 ...f,
columnName: formData.fieldName,
default: formData.formula,
isModified: true
}
                        : f
                )
            );
        } else {
            calcIdCounter += 1;
            const newCalc: SchemaCalculation = {
                id: `calc_${Date.now()}_${calcIdCounter}`,
                ...calcData,
                isNew: true,
            };
            setCalculations((prev) => [...prev, newCalc]);
            // Also add as an equation-type field
            setFields((prev) => [
                ...prev,
                {
                    id: `field_eq_${Date.now()}_${calcIdCounter}`,
                    columnName: formData.fieldName,
                    dataType: 'equation',
                    length: 0,
                    fieldsAccessBy: [],
                    description: '',
                    readOnly: true,
                    visibleStatus: true,
                    format: '',
                    default: formData.formula,
                    constraints: [],
                    references: {
 type: '',
table: '',
column: '',
values: []
},
                    validation: [],
                    isNew: true,
                    isModified: false,
                    equationFormula: formData.formula,
                },
            ]);
        }
        setShowModal(false);
    };

    const handleDelete = (calcId: string) => {
        const calc = calculations.find((c) => c.id === calcId);
        setCalculations((prev) => prev.filter((c) => c.id !== calcId));
        // Also remove the equation field
        if (calc) {
            setFields((prev) => prev.filter(
                (f) => !(f.dataType === 'equation' && f.columnName === calc.fieldName)
            ));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                    Define computed fields based on formulas.
                </p>
                <Button size="sm" onClick={openAddModal}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add Calculated Field
                </Button>
            </div>

            {calculations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 border rounded-md">
                    <Calculator className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No calculated fields defined.</p>
                    <Button size="sm" variant="outline" onClick={openAddModal}>
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add Your First Calculation
                    </Button>
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Field Name</TableHead>
                                <TableHead>Formula</TableHead>
                                <TableHead>Dependencies</TableHead>
                                <TableHead>Return Type</TableHead>
                                <TableHead className="w-[100px] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {calculations.map((calc) => (
                                <TableRow key={calc.id}>
                                    <TableCell className="font-medium">{calc.fieldName}</TableCell>
                                    <TableCell>
                                        <code className="text-xs bg-muted px-2 py-0.5 rounded max-w-[200px] truncate block">
                                            {calc.formula}
                                        </code>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {calc.dependencies.length > 0
                                                ? calc.dependencies.map((dep) => (
                                                    <Badge key={dep} variant="outline" className="text-xs">
                                                        {dep}
                                                    </Badge>
                                                ))
                                                : <span className="text-xs text-muted-foreground">-</span>}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="text-xs">
                                            {calc.returnType}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => openEditModal(calc)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-red-500 hover:text-red-700"
                                                onClick={() => handleDelete(calc.id)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Add/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingCalc ? 'Edit Calculated Field' : 'Add Calculated Field'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label className="text-sm">Field Name</Label>
                            <Input
                                value={formData.fieldName}
                                onChange={(e) => setFormData((p) => ({
 ...p,
fieldName: e.target.value
}))}
                                placeholder="e.g., fullName, totalPrice"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm">Formula</Label>
                            <Textarea
                                value={formData.formula}
                                onChange={(e) => setFormData((p) => ({
 ...p,
formula: e.target.value
}))}
                                placeholder='e.g., CONCAT(first_name, " ", last_name)'
                                rows={4}
                                className="font-mono text-sm resize-none"
                            />
                            <p className="text-xs text-muted-foreground">
                                Use field names from your schema. Functions: CONCAT, IF, SUM, AVG, COUNT.
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm">Dependencies (auto-detected)</Label>
                            <div className="flex flex-wrap gap-1 min-h-[32px] p-2 border rounded-md bg-muted/30">
                                {detectedDependencies.length > 0
                                    ? detectedDependencies.map((dep) => (
                                        <Badge key={dep} variant="secondary" className="text-xs">
                                            {dep}
                                        </Badge>
                                    ))
                                    : <span className="text-xs text-muted-foreground">No dependencies detected</span>}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-sm">Return Type</Label>
                            <Select
                                value={formData.returnType}
                                onValueChange={(v) => setFormData((p) => ({
 ...p,
returnType: v
}))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {dataTypes
                                        .filter((dt) => ['string', 'integer', 'decimal', 'boolean', 'datetime'].includes(dt.value))
                                        .map((dt) => (
                                            <SelectItem key={dt.value} value={dt.value}>
                                                {dt.label}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!formData.fieldName || !formData.formula}
                        >
                            {editingCalc ? 'Update' : 'Add'} Calculation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SchemaCalculationsTab;
